import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, NavLink, Routes, Route, Link } from "react-router-dom";
import { LayoutDashboard, Image as ImageIcon, MessageSquare, Crown, BarChart3, LogOut, Save, Plus, X, CalendarClock, Settings as SettingsIcon, CheckCircle2, XCircle, KeyRound, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import NativeSelect from "@/components/forms/NativeSelect";
import ImageUploader from "@/components/forms/ImageUploader";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import ChangePasswordDialog from "@/components/auth/ChangePasswordDialog";
import SubscriptionStatusCard from "@/components/owner/SubscriptionStatusCard";
import { useNoIndex } from "@/hooks/use-no-index";

function Sidebar() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  // Phone-only owners (registered via OTP-only path before email signup) may
  // not have a password yet → let them SET one without typing a current one.
  const requireCurrent = Boolean(user?.email);
  const item = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${isActive ? "bg-[#1F4E3D] text-white" : "text-[#1F2937] hover:bg-[#F5EFE6]"}`;
  return (
    <aside className="w-full lg:w-60 lg:shrink-0" data-testid="owner-dashboard-sidebar">
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-3 lg:sticky lg:top-20">
        <div className="px-3 py-2">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Spa Owner</div>
          <div className="font-heading text-base">{user?.name}</div>
        </div>
        <nav className="mt-2 space-y-1">
          <NavLink to="/dashboard" end className={item}><LayoutDashboard size={16}/> Listing</NavLink>
          <NavLink to="/dashboard/gallery" className={item}><ImageIcon size={16}/> Gallery</NavLink>
          <NavLink to="/dashboard/leads" className={item}><MessageSquare size={16}/> Leads</NavLink>
          <NavLink to="/dashboard/bookings" className={item}><CalendarClock size={16}/> Bookings</NavLink>
          <NavLink to="/dashboard/reviews" className={item}><Star size={16}/> Reviews</NavLink>
          <NavLink to="/dashboard/booking-settings" className={item}><SettingsIcon size={16}/> Booking Settings</NavLink>
          <NavLink to="/dashboard/analytics" className={item}><BarChart3 size={16}/> Analytics</NavLink>
          <NavLink to="/dashboard/plans" className={item}><Crown size={16}/> Plans</NavLink>
        </nav>
        <div className="mt-4 space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2 border-[#E7DDCF] text-[#1F2937]"
            onClick={() => setPwOpen(true)}
            data-testid="owner-change-password-button"
          >
            <KeyRound size={14}/> {requireCurrent ? "Change Password" : "Set Password"}
          </Button>
          <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => { logout(); nav("/"); }} data-testid="owner-logout-button">
            <LogOut size={14}/> Logout
          </Button>
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} requireCurrent={requireCurrent} />
    </aside>
  );
}

const CATEGORIES = ["Luxury Spa", "Ayurvedic Spa", "Thai Spa", "Massage Center", "Deep Tissue Massage", "Couples Spa", "Wellness Spa", "Day Spa", "Reflexology Spa", "Aromatherapy Spa", "Body Massage Center", "Foot Spa", "Beauty & Spa Center"];

/**
 * Parse legacy opening-hours strings into 24h HH:MM components for time
 * inputs. Accepts shapes like "10:00 AM - 11:00 PM", "10:00 - 22:00",
 * "10AM-10PM", or just a single time. Falls back to (10:00, 22:00).
 */
function parseOpeningHours(s) {
  const FALLBACK = { open: "10:00", close: "22:00" };
  if (!s || typeof s !== "string") return FALLBACK;
  // Split on dash / hyphen / en-dash / em-dash.
  const parts = s.split(/[-\u2013\u2014]/);
  if (parts.length < 2) return FALLBACK;
  const to24h = (raw) => {
    const t = raw.trim();
    if (!t) return null;
    // "10:00 AM" → 10:00, "1 PM" → 13:00, "22:00" → 22:00
    const m = t.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM|am|pm)?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const mm = m[2] ? parseInt(m[2], 10) : 0;
    const ap = m[3] ? m[3].toUpperCase() : null;
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };
  const open = to24h(parts[0]);
  const close = to24h(parts[1]);
  if (!open || !close) return FALLBACK;
  return { open, close };
}

/** Compose 24h HH:MM strings back into the canonical "HH:MM - HH:MM" form. */
function formatOpeningHours(open, close) {
  return `${open || "10:00"} - ${close || "22:00"}`;
}

/**
 * Parse a legacy price_range value (₹, ₹₹, ₹₹₹, "From ₹500", "₹500+", "500")
 * and return the numeric "starting from" rupee amount, or "" if unknown.
 */
function parseStartingPrice(s) {
  if (s == null) return "";
  const str = String(s);
  const m = str.match(/(\d+)/);
  return m ? m[1] : "";
}

/** Format a numeric rupees value into the canonical "From ₹500" string. */
function formatStartingPrice(amount) {
  const n = parseInt(amount, 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `From ₹${n}`;
}

const PLAN_LABEL = { free: "Free", featured: "Featured", premium: "Premium" };
const PLAN_BADGE_CLASS = {
  free: "bg-[#F5EFE6] text-[#7A5A22] border-[#E7DDCF]",
  featured: "bg-[#1F4E3D] text-white border-[#1F4E3D]",
  premium: "bg-[#C9A86A] text-[#111827] border-[#C9A86A]",
};

/**
 * Render PCMC + Pune <optgroup>s for a <select>. Defined at module scope so
 * React doesn't see a fresh component type on every parent render (which would
 * tear down and rebuild the <NativeSelect> subtree).
 */
function LocalityOptions({ localities }) {
  return (
    <>
      {localities.pcmc.length > 0 && (
        <optgroup label="PCMC">
          {localities.pcmc.map((l) => <option key={l} value={l}>{l}</option>)}
        </optgroup>
      )}
      {localities.pune.length > 0 && (
        <optgroup label="Pune">
          {localities.pune.map((l) => <option key={l} value={l}>{l}</option>)}
        </optgroup>
      )}
    </>
  );
}

function PlanLimitsBanner({ spa }) {
  if (!spa) return null;
  const plan = spa.plan || "free";
  const limits = spa.plan_limits || {};
  const galleryUsedMb = ((limits.gallery_used_bytes || 0) / (1024 * 1024)).toFixed(1);
  const galleryCapMb = limits.max_gallery_bytes ? (limits.max_gallery_bytes / (1024 * 1024)).toFixed(0) : null;
  const galleryCount = (spa.gallery || []).length;
  const servicesCount = (spa.services || []).length;
  const showUpgrade = plan === "free";

  const fmt = (used, cap, suffix = "") => cap === 0 ? `${used}${suffix} / Unlimited` : `${used}${suffix} / ${cap}${suffix}`;

  return (
    <div
      className="rounded-2xl bg-white border border-[#E7DDCF] p-4 sm:p-5 mb-5"
      data-testid="owner-plan-limits-banner"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-[#7A5A22]">Current Plan</span>
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${PLAN_BADGE_CLASS[plan]}`}
          data-testid="owner-current-plan-badge"
        >
          <Crown size={12} /> {PLAN_LABEL[plan]}
        </span>
        {showUpgrade && (
          <a href="/dashboard/plans" className="ml-auto">
            <Button size="sm" className="bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] h-8 text-xs" data-testid="owner-upgrade-cta">
              Upgrade
            </Button>
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-sm">
        <div className="rounded-xl bg-[#FBF7F1] border border-[#E7DDCF] p-3" data-testid="limit-card-images">
          <div className="text-xs text-[#4B5563]">Gallery images</div>
          <div className="font-medium text-[#111827] mt-0.5">{fmt(galleryCount, limits.max_gallery ?? 0)}</div>
        </div>
        <div className="rounded-xl bg-[#FBF7F1] border border-[#E7DDCF] p-3" data-testid="limit-card-size">
          <div className="text-xs text-[#4B5563]">Total gallery size</div>
          <div className="font-medium text-[#111827] mt-0.5">
            {galleryCapMb ? `${galleryUsedMb} MB / ${galleryCapMb} MB` : `${galleryUsedMb} MB / Unlimited`}
          </div>
        </div>
        <div className="rounded-xl bg-[#FBF7F1] border border-[#E7DDCF] p-3" data-testid="limit-card-services">
          <div className="text-xs text-[#4B5563]">Services</div>
          <div className="font-medium text-[#111827] mt-0.5">{fmt(servicesCount, limits.max_services ?? 0)}</div>
        </div>
      </div>
      {/* Feature gate indicators — make the matrix explicit so owners see exactly what they unlock per tier. */}
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs" data-testid="owner-plan-feature-gates">
        <FeatureChip enabled={!!limits.leads_enabled} label="Inquiries" />
        <FeatureChip enabled={!!limits.whatsapp_enabled} label="WhatsApp CTA" />
        <FeatureChip enabled={!!limits.booking_enabled} label="Online Booking" />
      </div>
      {plan === "free" && (
        <div className="mt-3 text-xs text-[#4B5563]" data-testid="owner-free-plan-note">
          On the Free plan, customer inquiries are disabled on your public listing. Upgrade to Featured to enable inquiries, or to Premium for WhatsApp + Online Booking.
        </div>
      )}
      {plan === "featured" && (
        <div className="mt-3 text-xs text-[#4B5563]" data-testid="owner-featured-plan-note">
          Featured plan includes inquiries. Upgrade to Premium to enable WhatsApp CTA and Online Booking on your listing.
        </div>
      )}
    </div>
  );
}

/** Small enabled/disabled chip used in PlanLimitsBanner. */
function FeatureChip({ enabled, label }) {
  const Icon = enabled ? CheckCircle2 : XCircle;
  return (
    <div
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 ${enabled ? "bg-[#EAF4EF] border-[#C9DFD2] text-[#1F4E3D]" : "bg-[#FBF7F1] border-[#E7DDCF] text-[#7A5A22]/70"}`}
      data-testid={`feature-chip-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <Icon size={12} />
      <span className="truncate">{label}</span>
    </div>
  );
}

function EditListing() {
  const [spa, setSpa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localities, setLocalities] = useState({ pcmc: [], pune: [] });
  const [categories, setCategories] = useState(CATEGORIES);
  const [creating, setCreating] = useState(false);
  const [newSpa, setNewSpa] = useState({ spa_name: "", category: "Luxury Spa", locality: "Wakad", city: "Pune", address: "", phone: "", description: "", services: [], opening_hours: "10:00 AM - 10:00 PM", price_range: "₹₹", gallery: [] });
  const [servicesStr, setServicesStr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/owner/spa").then((r) => {
      if (cancelled) return;
      setSpa(r.data.spa);
      setLoading(false);
      if (r.data.spa) setServicesStr((r.data.spa.services || []).join(", "));
    }).catch(() => { if (!cancelled) setLoading(false); });
    api.get("/meta/localities").then((r) => {
      if (!cancelled) setLocalities({ pcmc: r.data.pcmc || [], pune: r.data.pune || [] });
    }).catch(() => {});
    api.get("/meta/categories").then((r) => {
      if (!cancelled && r.data.categories?.length) setCategories(r.data.categories);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /** Render PCMC + Pune as <optgroup>s — module-scope component used inline below. */

  const save = async () => {
    setSaving(true);
    try {
      const parsedServices = servicesStr.split(",").map(s => s.trim()).filter(Boolean);
      const planMaxServices = spa?.plan_limits?.max_services;
      // Preemptively warn the owner if we're about to send more services than
      // their plan allows — the backend silently truncates, so an early toast
      // avoids "I typed 12, why do I see 10?" confusion.
      if (planMaxServices && planMaxServices > 0 && parsedServices.length > planMaxServices) {
        toast.error(
          `Your ${spa.plan} plan allows up to ${planMaxServices} services — only the first ${planMaxServices} will be kept.`,
        );
      }
      const payload = { ...spa, services: parsedServices };
      const res = await api.put("/owner/spa", payload);
      setSpa(res.data);
      setServicesStr((res.data.services || []).join(", "));
      toast.success("Listing saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = { ...newSpa, services: newSpa.services || [] };
      const res = await api.post("/owner/spa", payload);
      setSpa(res.data);
      setServicesStr((res.data.services || []).join(", "));
      toast.success("Spa listing created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  if (loading) return <div className="py-12 text-center text-[#4B5563]">Loading...</div>;

  if (!spa) {
    return (
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6" data-testid="owner-create-spa-form">
        <h2 className="font-heading text-2xl">Create Your Spa Listing</h2>
        <p className="text-sm text-[#4B5563] mb-5">Fill the basics. You can edit anything later.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Spa Name *</Label><Input value={newSpa.spa_name} onChange={(e) => setNewSpa({...newSpa, spa_name: e.target.value})} data-testid="create-spa-name-input" /></div>
          <div><Label className="text-xs">Phone *</Label><Input value={newSpa.phone} onChange={(e) => setNewSpa({...newSpa, phone: e.target.value})} data-testid="create-spa-phone-input" /></div>
          <div>
            <Label className="text-xs">Category</Label>
            <NativeSelect
              value={newSpa.category}
              onChange={(v) => setNewSpa({...newSpa, category: v})}
              options={categories}
              className="mt-1.5"
              data-testid="create-spa-category-select"
            />
          </div>
          <div>
            <Label className="text-xs">Locality</Label>
            <NativeSelect
              value={newSpa.locality}
              onChange={(v) => setNewSpa({...newSpa, locality: v})}
              className="mt-1.5"
              data-testid="create-spa-locality-select"
            >
              <LocalityOptions localities={localities} />
            </NativeSelect>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Address <span className="text-[#4B5563] font-normal">(Copy address mentioned in the Google Business Profile)</span></Label><Input value={newSpa.address} onChange={(e) => setNewSpa({...newSpa, address: e.target.value})} data-testid="create-spa-address-input" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={3} value={newSpa.description} onChange={(e) => setNewSpa({...newSpa, description: e.target.value})} data-testid="create-spa-description-input" /></div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Cover Image &amp; Gallery</Label>
            <ImageUploader
              value={newSpa.gallery || []}
              onChange={(next) => setNewSpa({ ...newSpa, gallery: next })}
              multiple
              maxFiles={10}
              aspectRatio="square"
              testId="create-spa-gallery-uploader"
              className="mt-1.5"
            />
          </div>
        </div>
        <Button onClick={create} disabled={creating} className="mt-5 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2" data-testid="create-spa-submit-button"><Plus size={14}/> {creating ? "Creating..." : "Create Listing"}</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SubscriptionStatusCard />
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6" data-testid="owner-edit-spa-form">
        <PlanLimitsBanner spa={spa} />
        <h2 className="font-heading text-2xl">Edit Listing</h2>
        <p className="text-sm text-[#4B5563] mb-5">Public URL: <span className="text-[#1F4E3D]">/spa/{spa.slug}</span></p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label className="text-xs">Spa Name</Label><Input value={spa.spa_name} onChange={(e) => setSpa({...spa, spa_name: e.target.value})} data-testid="edit-spa-name-input" /></div>
        <div><Label className="text-xs">Phone</Label><Input value={spa.phone} onChange={(e) => setSpa({...spa, phone: e.target.value})} /></div>
        <div><Label className="text-xs">WhatsApp</Label><Input value={spa.whatsapp || ""} onChange={(e) => setSpa({...spa, whatsapp: e.target.value})} /></div>
        <div><Label className="text-xs">Email</Label><Input value={spa.email || ""} onChange={(e) => setSpa({...spa, email: e.target.value})} /></div>
        <div>
          <Label className="text-xs">Category</Label>
          <NativeSelect
            value={spa.category}
            onChange={(v) => setSpa({...spa, category: v})}
            options={categories}
            className="mt-1.5"
            data-testid="edit-spa-category-select"
          />
        </div>
        <div>
          <Label className="text-xs">Locality</Label>
          <NativeSelect
            value={spa.locality}
            onChange={(v) => setSpa({...spa, locality: v})}
            className="mt-1.5"
            data-testid="edit-spa-locality-select"
          >
            <LocalityOptions localities={localities} />
          </NativeSelect>
        </div>
        <div className="sm:col-span-2"><Label className="text-xs">Address <span className="text-[#4B5563] font-normal">(Copy address mentioned in the Google Business Profile)</span></Label><Input value={spa.address} onChange={(e) => setSpa({...spa, address: e.target.value})} /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={4} value={spa.description} onChange={(e) => setSpa({...spa, description: e.target.value})} /></div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Services (comma-separated)</Label>
          <Textarea
            rows={2}
            value={servicesStr}
            onChange={(e) => setServicesStr(e.target.value)}
            placeholder="Swedish Massage, Deep Tissue, Aromatherapy"
            data-testid="edit-spa-services-input"
          />
          {(() => {
            const currentCount = servicesStr.split(",").map((s) => s.trim()).filter(Boolean).length;
            const maxSvc = spa?.plan_limits?.max_services;
            if (!maxSvc || maxSvc <= 0) return null;
            const over = currentCount > maxSvc;
            return (
              <div
                className={`text-[11px] mt-1 flex items-center justify-between ${over ? "text-[#B91C1C]" : "text-[#7A5A22]"}`}
                data-testid="services-count-hint"
              >
                <span>
                  {spa.plan?.charAt(0).toUpperCase() + spa.plan?.slice(1)} plan allows up to {maxSvc} services.
                </span>
                <span data-testid="services-count-badge">{currentCount}/{maxSvc}</span>
              </div>
            );
          })()}
        </div>
        <div>
          <Label className="text-xs">Opening Hours</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <div>
              <Input
                type="time"
                value={parseOpeningHours(spa.opening_hours).open}
                onChange={(e) => {
                  const close = parseOpeningHours(spa.opening_hours).close;
                  setSpa({ ...spa, opening_hours: formatOpeningHours(e.target.value, close) });
                }}
                data-testid="edit-spa-opening-time"
                aria-label="Opening time"
              />
              <div className="text-[10px] uppercase tracking-widest text-[#7A5A22] mt-1">Opens</div>
            </div>
            <div>
              <Input
                type="time"
                value={parseOpeningHours(spa.opening_hours).close}
                onChange={(e) => {
                  const open = parseOpeningHours(spa.opening_hours).open;
                  setSpa({ ...spa, opening_hours: formatOpeningHours(open, e.target.value) });
                }}
                data-testid="edit-spa-closing-time"
                aria-label="Closing time"
              />
              <div className="text-[10px] uppercase tracking-widest text-[#7A5A22] mt-1">Closes</div>
            </div>
          </div>
        </div>
        <div>
          <Label className="text-xs">Starting Price (₹)</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A5A22] text-sm pointer-events-none">₹</span>
            <Input
              type="number"
              min="0"
              step="50"
              placeholder="e.g. 500"
              value={parseStartingPrice(spa.price_range)}
              onChange={(e) => setSpa({ ...spa, price_range: formatStartingPrice(e.target.value) })}
              className="pl-7"
              data-testid="edit-spa-starting-price-input"
            />
          </div>
          <div className="text-[11px] text-[#4B5563] mt-1">
            Shown publicly as <span className="text-[#1F4E3D]">{spa.price_range || "From ₹—"}</span>
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 bg-[#111827] hover:bg-[#0B1220] text-white gap-2" data-testid="edit-spa-save-button"><Save size={14}/> {saving ? "Saving..." : "Save Changes"}</Button>
      </div>
    </div>
  );
}

function GalleryManager() {
  const [spa, setSpa] = useState(null);
  useEffect(() => { api.get("/owner/spa").then((r) => setSpa(r.data.spa)); }, []);
  if (!spa) return <div className="py-12 text-center">Create your listing first →</div>;

  const limits = spa.plan_limits || {};
  const maxFiles = limits.max_gallery && limits.max_gallery > 0 ? limits.max_gallery : 20;
  const maxBytes = limits.max_gallery_bytes && limits.max_gallery_bytes > 0 ? limits.max_gallery_bytes : undefined;
  const perImageBytes = limits.max_bytes_per_image && limits.max_bytes_per_image > 0 ? limits.max_bytes_per_image : undefined;
  const perImageMbLabel = perImageBytes
    ? `${(perImageBytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`
    : null;
  const totalMbLabel = maxBytes ? `${(maxBytes / (1024 * 1024)).toFixed(0)} MB` : null;

  /**
   * Persist a new gallery list. The cover image is preserved if the user's
   * currently-selected cover is still present in the new list. Otherwise we
   * fall back to the first image (or null if the gallery is now empty).
   */
  const setGallery = async (next) => {
    try {
      let cover = spa.cover_image;
      if (!cover || !next.includes(cover)) {
        cover = next[0] || null;
      }
      const r = await api.put("/owner/spa", { gallery: next, cover_image: cover });
      setSpa(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update gallery");
      // Refetch fresh state so UI reflects what's actually on the server.
      try {
        const fresh = await api.get("/owner/spa");
        setSpa(fresh.data.spa);
      } catch (refetchErr) {
        console.warn("Could not refetch owner spa after failed gallery update:", refetchErr);
      }
    }
  };

  /** Explicitly mark a gallery URL as the new cover image. */
  const setCover = async (url) => {
    if (!url || url === spa.cover_image) return;
    try {
      const r = await api.put("/owner/spa", { cover_image: url });
      setSpa(r.data);
      toast.success("Cover image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update cover image");
    }
  };

  return (
    <div data-testid="owner-gallery-page">
      <PlanLimitsBanner spa={spa} />
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6">
        <h2 className="font-heading text-2xl">Gallery</h2>
        <p className="text-sm text-[#4B5563] mb-4">
          Upload photos of your spa. Hover any image and click <span className="font-medium text-[#7A5A22]">Set cover</span> to choose which one appears as the main listing photo.
          <span className="block mt-1 text-[#7A5A22]" data-testid="gallery-plan-hint">
            {spa.plan?.charAt(0).toUpperCase() + spa.plan?.slice(1)} plan allows up to {maxFiles} images
            {perImageMbLabel ? `, ${perImageMbLabel} per image` : ""}
            {totalMbLabel ? `, and ${totalMbLabel} total` : ""}.
          </span>
        </p>
        <ImageUploader
          value={spa.gallery || []}
          onChange={setGallery}
          multiple
          maxFiles={maxFiles}
          maxBytesPerImage={perImageBytes}
          aspectRatio="square"
          testId="owner-gallery-uploader"
          coverUrl={spa.cover_image}
          onSetCover={setCover}
        />
      </div>
    </div>
  );
}

function LeadsView() {
  const [items, setItems] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  useEffect(() => { api.get("/owner/leads").then((r) => setItems(r.data.items)); }, []);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete this lead from ${lead.name || "customer"}? This cannot be undone.`)) return;
    setDeletingId(lead.id);
    try {
      await api.delete(`/owner/leads/${lead.id}`);
      // Optimistic UI: strip locally rather than refetching the whole list.
      setItems((prev) => (prev || []).filter((l) => l.id !== lead.id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete lead");
    } finally {
      setDeletingId(null);
    }
  };

  if (items === null) return <div className="py-12 text-center">Loading...</div>;
  return (
    <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6" data-testid="owner-leads-page">
      <h2 className="font-heading text-2xl">Leads & Inquiries</h2>
      <p className="text-sm text-[#4B5563] mb-4">{items.length} inquiry{items.length === 1 ? "" : "s"} received</p>
      {items.length === 0 ? <div className="text-sm text-[#4B5563] py-8 text-center">No leads yet. Share your spa URL to start getting inquiries.</div> : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="rounded-xl border border-[#E7DDCF] p-4" data-testid="owner-lead-row">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-[#4B5563] break-all">☎ {l.phone} {l.email ? `· ${l.email}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-xs text-[#4B5563] hidden sm:block">{new Date(l.created_at).toLocaleString()}</div>
                  <button
                    type="button"
                    onClick={() => handleDelete(l)}
                    disabled={deletingId === l.id}
                    aria-label="Delete lead"
                    title="Delete lead"
                    data-testid={`delete-lead-${l.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#B42318] hover:bg-[#FEF3F2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="text-xs text-[#4B5563] mt-1 sm:hidden">{new Date(l.created_at).toLocaleString()}</div>
              <p className="text-sm text-[#1F2937] mt-2">{l.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsView() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/owner/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <div className="py-12 text-center">Loading...</div>;
  const cards = [
    { label: "Profile Views (est.)", value: a.profile_views },
    { label: "Leads", value: a.leads },
    { label: "Reviews", value: a.reviews },
    { label: "Rating", value: (a.rating || 0).toFixed(1) },
  ];
  return (
    <div data-testid="owner-analytics-page">
      <h2 className="font-heading text-2xl mb-5">Analytics</h2>
      <div className="grid sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-[#E7DDCF] p-4">
            <div className="text-xs text-[#4B5563]">{c.label}</div>
            <div className="font-heading text-3xl mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 text-xs text-[#4B5563] italic">Profile view estimates are derived from lead & review activity. Detailed analytics coming with Premium plan.</div>
    </div>
  );
}

function PlansView() {
  const [spa, setSpa] = useState(null);
  useEffect(() => { api.get("/owner/spa").then((r) => setSpa(r.data.spa)).catch(() => {}); }, []);
  const plan = spa?.plan || "free";
  return (
    <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6" data-testid="owner-plans-page">
      <h2 className="font-heading text-2xl">Plans</h2>
      <p className="text-sm text-[#4B5563] mt-1">
        Your current plan:{" "}
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${PLAN_BADGE_CLASS[plan]}`}
          data-testid="plans-page-current-plan-badge"
        >
          <Crown size={12}/> {PLAN_LABEL[plan]}
        </span>
      </p>
      {plan === "free" && (
        <div className="mt-3 text-sm text-[#4B5563]" data-testid="plans-page-free-note">
          Upgrade to <strong>Featured</strong> to unlock customer inquiries, more gallery images, and more services.
          Upgrade to <strong>Premium</strong> to additionally unlock <strong>WhatsApp CTA</strong> and <strong>Online Booking</strong>.
        </div>
      )}
      {plan === "featured" && (
        <div className="mt-3 text-sm text-[#4B5563]" data-testid="plans-page-featured-note">
          Upgrade to <strong>Premium</strong> to add the <strong>WhatsApp CTA</strong> and enable an <strong>Online Booking</strong> module on your listing.
        </div>
      )}
      <div className="mt-4"><a href="/plans"><Button className="bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]" data-testid="plans-page-view-plans-button">View All Plans</Button></a></div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * ReviewsView
 * -------------------------------------------------------------------------
 * Premium-only "Customer Reviews" tab in the Owner Dashboard.
 *
 * Data flow:
 *   1. On mount → GET /api/owner/reviews. If the caller isn't on a Premium
 *      plan the backend returns 402 → we render an inline upgrade card.
 *   2. Owner clicks a red trash button → an AlertDialog confirms the action.
 *   3. On confirm → DELETE /api/owner/reviews/{id}. The server returns the
 *      recomputed `rating` + `review_count` so the header updates without
 *      a follow-up round-trip. The row is stripped optimistically.
 *
 * Access-control safety: even if an attacker crafted a request with another
 * spa's review_id, the server enforces `spa_id == owner's spa`, so this
 * component can trust its own render logic here.
 * ------------------------------------------------------------------------- */

function StarRating({ value }) {
  // Purely visual 5-star row — filled stars up to `value` rounded to nearest int.
  const full = Math.round(value || 0);
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value || 0} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= full ? "fill-[#F5B301] text-[#F5B301]" : "text-[#E7DDCF]"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function ReviewUpgradeCard() {
  return (
    <div
      className="rounded-2xl border border-dashed border-[#E7DDCF] bg-gradient-to-br from-[#FBF7F1] to-white p-8 text-center"
      data-testid="reviews-upgrade-gate"
    >
      <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#F5EFE6] mb-4">
        <Crown size={20} className="text-[#7A5A22]" />
      </div>
      <h2 className="font-heading text-2xl text-[#111827]">Reputation Management is a Premium feature</h2>
      <p className="text-sm text-[#4B5563] max-w-md mx-auto mt-2">
        Upgrade to the Premium plan to view all customer reviews on your listing and
        permanently remove inappropriate ones. Ratings recalculate automatically.
      </p>
      <Link to="/plans">
        <Button className="mt-5 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2" data-testid="reviews-upgrade-cta">
          <Crown size={14}/> Upgrade to Premium
        </Button>
      </Link>
    </div>
  );
}

function ReviewsView() {
  const [items, setItems] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [locked, setLocked] = useState(false);  // true if backend returned 402
  const [pendingReview, setPendingReview] = useState(null);   // review awaiting confirmation
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/owner/reviews");
      setItems(data.items || []);
      setRating(data.rating || 0);
      setReviewCount(data.review_count ?? (data.items || []).length);
      setLocked(false);
    } catch (e) {
      if (e?.response?.status === 402) {
        setLocked(true);
        setItems([]);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load reviews");
        setItems([]);
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    if (!pendingReview) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/owner/reviews/${pendingReview.id}`);
      // Optimistic list update + trust server-recomputed aggregate.
      setItems((prev) => (prev || []).filter((r) => r.id !== pendingReview.id));
      setRating(data.rating ?? 0);
      setReviewCount(data.review_count ?? Math.max(0, reviewCount - 1));
      toast.success("Review deleted successfully.");
      setPendingReview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete review");
    } finally {
      setDeleting(false);
    }
  };

  if (items === null) return <div className="py-12 text-center">Loading...</div>;
  if (locked) return <ReviewUpgradeCard />;

  return (
    <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6" data-testid="owner-reviews-page">
      {/* Header — average rating + count */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-2xl">Customer Reviews</h2>
          <p className="text-sm text-[#4B5563] mt-1">
            Manage feedback for your spa. Deleted reviews are removed instantly from your public listing and your rating is recalculated.
          </p>
        </div>
        <div className="rounded-xl bg-[#F5EFE6] border border-[#E7DDCF] px-4 py-3 text-right min-w-[140px]">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Average Rating</div>
          <div className="flex items-center justify-end gap-2 mt-1">
            <span className="font-heading text-2xl text-[#111827]" data-testid="reviews-avg-rating">{Number(rating || 0).toFixed(1)}</span>
            <StarRating value={rating} />
          </div>
          <div className="text-xs text-[#4B5563] mt-0.5" data-testid="reviews-count">
            {reviewCount} review{reviewCount === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-sm text-[#4B5563] py-10 text-center">
          No reviews yet. As customers leave feedback, it will appear here for you to manage.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const when = r.created_at ? new Date(r.created_at) : null;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-[#E7DDCF] p-4 hover:border-[#D6C5A9] transition-colors"
                data-testid="owner-review-row"
              >
                <div className="flex flex-wrap gap-3 items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium text-[#111827]" data-testid="review-author">
                        {r.customer_name?.trim() || "Anonymous"}
                      </div>
                      <StarRating value={r.rating} />
                      <span className="text-xs text-[#4B5563]">({Number(r.rating || 0).toFixed(1)})</span>
                    </div>
                    {when && (
                      <div className="text-xs text-[#4B5563] mt-0.5">
                        {when.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPendingReview(r)}
                    className="gap-1.5 text-[#B42318] border-[#F1D5D5] hover:bg-[#FEF3F2] hover:text-[#912018]"
                    data-testid={`delete-review-${r.id}`}
                    aria-label="Delete this review"
                  >
                    <Trash2 size={13} /> Delete
                  </Button>
                </div>
                {r.review && (
                  <p className="text-sm text-[#1F2937] mt-3 whitespace-pre-wrap break-words">{r.review}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog — matches the exact copy from the spec. */}
      <AlertDialog open={!!pendingReview} onOpenChange={(v) => !v && !deleting && setPendingReview(null)}>
        <AlertDialogContent data-testid="delete-review-confirm-dialog" className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl text-[#111827]">
              Delete Review?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4B5563]">
              This action cannot be undone. Are you sure you want to permanently delete this review?
              {pendingReview?.customer_name && (
                <span className="block mt-2 text-[#111827]">
                  &ldquo;<span className="italic">{(pendingReview.review || "").slice(0, 140)}{(pendingReview.review || "").length > 140 ? "…" : ""}</span>&rdquo;
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="delete-review-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-[#B42318] hover:bg-[#912018] focus:ring-[#B42318]"
              data-testid="delete-review-confirm-button"
            >
              {deleting ? "Deleting..." : "Delete Review"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BookingsView() {
  const [items, setItems] = useState(null);
  const [spa, setSpa] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  useEffect(() => {
    api.get("/owner/spa").then((r) => setSpa(r.data.spa)).catch(() => {});
    api.get("/owner/bookings").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const handleDelete = async (booking) => {
    if (!window.confirm(`Delete booking for ${booking.customer_name} at ${booking.time_slot} on ${booking.date}? This cannot be undone.`)) return;
    setDeletingId(booking.id);
    try {
      await api.delete(`/owner/bookings/${booking.id}`);
      setItems((prev) => (prev || []).filter((b) => b.id !== booking.id));
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete booking");
    } finally {
      setDeletingId(null);
    }
  };

  // Group by date and pre-sort each day's slots so the JSX render is pure
  // iteration — no .sort() / .reduce() on every parent re-render.
  const grouped = useMemo(() => {
    const acc = {};
    for (const b of items || []) {
      (acc[b.date] = acc[b.date] || []).push(b);
    }
    // Sort each day's bookings by time slot (ascending) — copy first so we
    // don't mutate state-held arrays.
    for (const d of Object.keys(acc)) {
      acc[d] = [...acc[d]].sort((a, b) => a.time_slot.localeCompare(b.time_slot));
    }
    return acc;
  }, [items]);
  const dates = useMemo(() => Object.keys(grouped).sort().reverse(), [grouped]);

  if (items === null) return <div className="py-12 text-center">Loading...</div>;

  const bookingEnabled = !!spa?.plan_limits?.booking_enabled;

  return (
    <div data-testid="owner-bookings-page">
      {spa && <PlanLimitsBanner spa={spa} />}

      {!bookingEnabled && (
        <div
          className="rounded-2xl border border-dashed border-[#E7DDCF] bg-[#FBF7F1] p-5 mb-4 text-sm text-[#7A5A22]"
          data-testid="bookings-locked-note"
        >
          Online booking is a Premium-only feature. Upgrade to Premium to receive online appointment requests from customers.
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6">
        <h2 className="font-heading text-2xl">Bookings</h2>
        <p className="text-sm text-[#4B5563] mb-4">
          {items.length} appointment request{items.length === 1 ? "" : "s"} received
        </p>

        {items.length === 0 ? (
          <div className="text-sm text-[#4B5563] py-8 text-center" data-testid="bookings-empty">
            {bookingEnabled
              ? "No bookings yet. Customers can request appointments directly from your public listing."
              : "Upgrade to Premium to start receiving booking requests."}
          </div>
        ) : (
          <div className="space-y-6">
            {dates.map((d) => (
              <div key={d} data-testid={`bookings-date-group-${d}`}>
                <div className="text-xs uppercase tracking-widest text-[#7A5A22] mb-2">
                  {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "short", day: "numeric" })}
                </div>
                <div className="space-y-2">
                  {grouped[d].map((b) => (
                      <div
                        key={b.id}
                        className="rounded-xl border border-[#E7DDCF] p-4 flex flex-wrap gap-3 items-start"
                        data-testid="owner-booking-row"
                      >
                        <div className="text-center min-w-[64px]">
                          <div className="font-heading text-lg text-[#1F4E3D]">{b.time_slot}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#7A5A22]">slot</div>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <div className="font-medium text-[#111827]">{b.customer_name}</div>
                          <div className="text-xs text-[#4B5563]">
                            ☎ {b.customer_phone}{b.customer_email ? ` · ${b.customer_email}` : ""}
                          </div>
                          {b.service_name && (
                            <div className="text-xs text-[#4B5563] mt-0.5">Service: <span className="text-[#111827]">{b.service_name}</span></div>
                          )}
                          {b.notes && <p className="text-sm text-[#1F2937] mt-2">{b.notes}</p>}
                        </div>
                        <div className="flex items-start gap-2 shrink-0">
                          <div className="text-xs text-[#4B5563] pt-1">
                            Requested {new Date(b.created_at).toLocaleString()}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDelete(b)}
                            disabled={deletingId === b.id}
                            aria-label="Delete booking"
                            title="Delete booking"
                            data-testid={`delete-booking-${b.id}`}
                            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#B42318] hover:bg-[#FEF3F2] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const DAY_LABELS = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};
const DEFAULT_HOURS = {
  monday:    { open: "10:00", close: "21:00", closed: false },
  tuesday:   { open: "10:00", close: "21:00", closed: false },
  wednesday: { open: "10:00", close: "21:00", closed: false },
  thursday:  { open: "10:00", close: "21:00", closed: false },
  friday:    { open: "10:00", close: "21:00", closed: false },
  saturday:  { open: "10:00", close: "22:00", closed: false },
  sunday:    { open: "10:00", close: "22:00", closed: false },
};

function BookingSettings() {
  const [spa, setSpa] = useState(null);
  const [slotMinutes, setSlotMinutes] = useState(60);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/owner/spa").then((r) => {
      const s = r.data.spa;
      setSpa(s);
      const cfg = s?.booking_config;
      if (cfg) {
        setSlotMinutes(cfg.slot_duration_minutes || 60);
        setHours({ ...DEFAULT_HOURS, ...(cfg.working_hours || {}) });
      }
    }).catch(() => {});
  }, []);

  const updateDay = (day, patch) => setHours((h) => ({ ...h, [day]: { ...h[day], ...patch } }));

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await api.put("/owner/spa/booking-config", {
        slot_duration_minutes: slotMinutes,
        working_hours: hours,
      });
      setSpa(r.data);
      toast.success("Booking settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  }, [slotMinutes, hours]);

  if (!spa) return <div className="py-12 text-center">Loading...</div>;
  const bookingEnabled = !!spa.plan_limits?.booking_enabled;

  return (
    <div data-testid="owner-booking-settings-page">
      <PlanLimitsBanner spa={spa} />
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-6">
        <h2 className="font-heading text-2xl">Booking Settings</h2>
        <p className="text-sm text-[#4B5563] mb-5">
          Define your working hours and slot length. Customers will be able to pick an available slot when they book on your listing.
        </p>

        {!bookingEnabled && (
          <div
            className="rounded-xl border border-dashed border-[#E7DDCF] bg-[#FBF7F1] p-4 mb-5 text-sm text-[#7A5A22]"
            data-testid="booking-settings-locked"
          >
            Online booking is a <strong>Premium</strong>-only feature. You can configure your hours below, but they&apos;ll only be used once you upgrade.
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3 items-end mb-4">
          <div className="sm:col-span-1">
            <Label className="text-xs">Slot length</Label>
            <NativeSelect
              value={String(slotMinutes)}
              onChange={(v) => setSlotMinutes(parseInt(v, 10))}
              data-testid="booking-slot-duration-select"
            >
              <option value="30">30 minutes</option>
              <option value="60">60 minutes</option>
            </NativeSelect>
          </div>
        </div>

        <div className="space-y-2" data-testid="booking-working-hours-grid">
          {Object.keys(DAY_LABELS).map((day) => {
            const h = hours[day] || DEFAULT_HOURS[day];
            return (
              <div
                key={day}
                className="grid grid-cols-1 sm:grid-cols-[120px_120px_120px_1fr] gap-2 items-center rounded-xl border border-[#E7DDCF] px-3 py-2"
                data-testid={`booking-working-day-${day}`}
              >
                <div className="text-sm font-medium text-[#111827]">{DAY_LABELS[day]}</div>
                <Input
                  type="time"
                  value={h.open}
                  disabled={h.closed}
                  onChange={(e) => updateDay(day, { open: e.target.value })}
                  data-testid={`booking-${day}-open`}
                />
                <Input
                  type="time"
                  value={h.close}
                  disabled={h.closed}
                  onChange={(e) => updateDay(day, { close: e.target.value })}
                  data-testid={`booking-${day}-close`}
                />
                <label className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <input
                    type="checkbox"
                    checked={!!h.closed}
                    onChange={(e) => updateDay(day, { closed: e.target.checked })}
                    data-testid={`booking-${day}-closed`}
                  />
                  Closed all day
                </label>
              </div>
            );
          })}
        </div>

        <div className="mt-5">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
            data-testid="booking-settings-save-button"
          >
            <Save size={14}/> {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  useNoIndex();
  useEffect(() => {
    document.title = "Spa Owner Dashboard - Pune Spa Guide";
    if (!user) nav("/login");
    else if (user.role !== "owner" && user.role !== "admin") nav("/");
  }, [user, nav]);
  if (!user) return null;
  return (
    <div data-testid="owner-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-6">
        <Sidebar/>
        <div className="flex-1">
          <Routes>
            <Route index element={<EditListing/>} />
            <Route path="gallery" element={<GalleryManager/>} />
            <Route path="leads" element={<LeadsView/>} />
            <Route path="bookings" element={<BookingsView/>} />
            <Route path="reviews" element={<ReviewsView/>} />
            <Route path="booking-settings" element={<BookingSettings/>} />
            <Route path="analytics" element={<AnalyticsView/>} />
            <Route path="plans" element={<PlansView/>} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
